import React, { useState } from 'react';
import { Calendar, Plus, Trash2 } from 'lucide-react';

interface BlockedTime {
  id: string;
  start: Date;
  end: Date;
  reason: string;
  recurring?: boolean;
  recurrenceEnd?: Date;
  allDay?: boolean;
}

interface BlockedTimesTabProps {
  blockedTimes: BlockedTime[];
  onUpdate: (times: BlockedTime[]) => void;
}

const INITIAL_BLOCKED_TIME = {
  start: '',
  end: '',
  date: '',
  dateRange: {
    start: '',
    end: ''
  },
  reason: '',
  recurring: false,
  recurrenceEnd: '',
  allDay: false,
  isDateRange: false
};

export const BlockedTimesTab: React.FC<BlockedTimesTabProps> = ({ blockedTimes, onUpdate }) => {
  const [newBlockedTime, setNewBlockedTime] = useState(INITIAL_BLOCKED_TIME);

  const handleAddBlockedTime = () => {
    if ((!newBlockedTime.start || !newBlockedTime.end) && 
        !newBlockedTime.date && 
        (!newBlockedTime.dateRange.start || !newBlockedTime.dateRange.end) || 
        !newBlockedTime.reason) return;

    let newTimes: BlockedTime[] = [];

    if (newBlockedTime.isDateRange) {
      const startDate = new Date(newBlockedTime.dateRange.start);
      const endDate = new Date(newBlockedTime.dateRange.end);

      for (let date = startDate; date <= endDate; date.setDate(date.getDate() + 1)) {
        const dayStart = new Date(date);
        dayStart.setHours(0, 0, 0, 0);
        const dayEnd = new Date(date);
        dayEnd.setHours(23, 59, 59, 999);

        newTimes.push({
          id: `${Date.now()}-${date.getTime()}`,
          start: dayStart,
          end: dayEnd,
          reason: newBlockedTime.reason,
          allDay: true
        });
      }
    } else if (newBlockedTime.allDay && newBlockedTime.date) {
      const startDate = new Date(newBlockedTime.date);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(newBlockedTime.date);
      endDate.setHours(23, 59, 59, 999);

      newTimes.push({
        id: Date.now().toString(),
        start: startDate,
        end: endDate,
        reason: newBlockedTime.reason,
        allDay: true
      });
    } else {
      newTimes.push({
        id: Date.now().toString(),
        start: new Date(newBlockedTime.start),
        end: new Date(newBlockedTime.end),
        reason: newBlockedTime.reason,
        recurring: newBlockedTime.recurring,
        allDay: false,
        ...(newBlockedTime.recurring && newBlockedTime.recurrenceEnd
          ? { recurrenceEnd: new Date(newBlockedTime.recurrenceEnd) }
          : {})
      });
    }

    onUpdate([...blockedTimes, ...newTimes]);
    setNewBlockedTime(INITIAL_BLOCKED_TIME);
  };

  const handleRemoveBlockedTime = (id: string) => {
    onUpdate(blockedTimes.filter(time => time.id !== id));
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 p-4">
        <h4 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white mb-4">Neue Sperrzeit</h4>
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <label className="flex items-center">
              <input
                type="radio"
                checked={!newBlockedTime.isDateRange && !newBlockedTime.allDay}
                onChange={() => setNewBlockedTime(prev => ({
                  ...prev,
                  isDateRange: false,
                  allDay: false,
                  date: '',
                  dateRange: { start: '', end: '' }
                }))}
                className="rounded-full border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Zeitraum</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                checked={!newBlockedTime.isDateRange && newBlockedTime.allDay}
                onChange={() => setNewBlockedTime(prev => ({
                  ...prev,
                  isDateRange: false,
                  allDay: true,
                  start: '',
                  end: '',
                  dateRange: { start: '', end: '' }
                }))}
                className="rounded-full border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Ganzer Tag</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                checked={newBlockedTime.isDateRange}
                onChange={() => setNewBlockedTime(prev => ({
                  ...prev,
                  isDateRange: true,
                  allDay: false,
                  start: '',
                  end: '',
                  date: ''
                }))}
                className="rounded-full border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Datumsbereich</span>
            </label>
          </div>

          {newBlockedTime.isDateRange ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Von</label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Calendar className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  </div>
                  <input
                    type="date"
                    value={newBlockedTime.dateRange.start}
                    onChange={(e) => setNewBlockedTime(prev => ({
                      ...prev,
                      dateRange: { ...prev.dateRange, start: e.target.value }
                    }))}
                    className="block w-full pl-10 pr-3 py-2 sm:py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-base sm:text-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Bis</label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Calendar className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  </div>
                  <input
                    type="date"
                    value={newBlockedTime.dateRange.end}
                    min={newBlockedTime.dateRange.start}
                    onChange={(e) => setNewBlockedTime(prev => ({
                      ...prev,
                      dateRange: { ...prev.dateRange, end: e.target.value }
                    }))}
                    className="block w-full pl-10 pr-3 py-2 sm:py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-base sm:text-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>
            </div>
          ) : newBlockedTime.allDay ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Datum</label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Calendar className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                </div>
                <input
                  type="date"
                  value={newBlockedTime.date}
                  onChange={(e) => setNewBlockedTime(prev => ({ ...prev, date: e.target.value }))}
                  className="block w-full pl-10 pr-3 py-2 sm:py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-base sm:text-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Start</label>
                <input
                  type="datetime-local"
                  value={newBlockedTime.start}
                  onChange={(e) => setNewBlockedTime(prev => ({ ...prev, start: e.target.value }))}
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white text-base sm:text-sm py-2 sm:py-1.5"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Ende</label>
                <input
                  type="datetime-local"
                  value={newBlockedTime.end}
                  min={newBlockedTime.start}
                  onChange={(e) => setNewBlockedTime(prev => ({ ...prev, end: e.target.value }))}
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white text-base sm:text-sm py-2 sm:py-1.5"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Grund</label>
            <input
              type="text"
              value={newBlockedTime.reason}
              onChange={(e) => setNewBlockedTime(prev => ({ ...prev, reason: e.target.value }))}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white text-base sm:text-sm py-2 sm:py-1.5"
              placeholder="z.B. Wartung, Training, Turnier"
            />
          </div>

          {!newBlockedTime.allDay && !newBlockedTime.isDateRange && (
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={newBlockedTime.recurring}
                  onChange={(e) => setNewBlockedTime(prev => ({ 
                    ...prev, 
                    recurring: e.target.checked,
                    recurrenceEnd: e.target.checked ? prev.recurrenceEnd : ''
                  }))}
                  className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Wöchentlich wiederholen</span>
              </label>

              {newBlockedTime.recurring && (
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Wiederholen bis</label>
                  <input
                    type="date"
                    value={newBlockedTime.recurrenceEnd}
                    onChange={(e) => setNewBlockedTime(prev => ({ ...prev, recurrenceEnd: e.target.value }))}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white text-base sm:text-sm py-2 sm:py-1.5"
                  />
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end pt-2">
            <button
              type="button"
              onClick={handleAddBlockedTime}
              className="inline-flex items-center px-4 py-2.5 sm:py-2 border border-transparent rounded-md shadow-sm text-base sm:text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 w-full sm:w-auto justify-center"
            >
              <Plus className="h-5 w-5 mr-2" />
              Sperrzeit hinzufügen
            </button>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h4 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white">Aktuelle Sperrzeiten</h4>
        {blockedTimes.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Keine Sperrzeiten vorhanden</p>
        ) : (
          blockedTimes
            .sort((a, b) => a.start.getTime() - b.start.getTime())
            .map((time) => (
              <div key={time.id} className="flex flex-col sm:flex-row sm:items-center justify-between bg-gray-50 dark:bg-gray-700 p-4 rounded-lg gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {time.start.toLocaleDateString('de-DE', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-300">
                      {time.start.toLocaleTimeString('de-DE', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })} - {time.end.toLocaleTimeString('de-DE', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })} Uhr
                    </span>
                    {time.recurring && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                        Wöchentlich
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{time.reason}</p>
                  {time.recurring && time.recurrenceEnd && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Wiederholt sich bis zum {time.recurrenceEnd.toLocaleDateString('de-DE', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveBlockedTime(time.id)}
                  className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 w-full sm:w-auto"
                >
                  <Trash2 className="h-5 w-5 sm:mr-2" />
                  <span className="hidden sm:inline">Entfernen</span>
                </button>
              </div>
            ))
        )}
      </div>
    </div>
  );
};